"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from "react";

import { cn } from "../utils";

const RadioButtonGroup = forwardRef<
	ElementRef<typeof RadioGroupPrimitive.Root>,
	ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
	return (
		<RadioGroupPrimitive.Root
			className={cn("group", "grid", "gap-2", "text-xs", className)}
			{...props}
			ref={ref}
		/>
	);
});
RadioButtonGroup.displayName = "RadioButtonGroup";

const RadioButtonGroupItem = forwardRef<
	ElementRef<typeof RadioGroupPrimitive.Item>,
	ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
	return (
		<RadioGroupPrimitive.Item
			ref={ref}
			className={cn(
				"rounded-md",
				"p-2",
				"flex",
				"items-center",
				"font-normal",
				"gap-2",
				"transition-[background-color,border-color,outline-color]",
				"duration-150",

				"border",
				"border-palette-gray-7",
				"hover:border-palette-gray-8",
				"focus:border-palette-brand-8",
				"active:border-palette-brand-7",
				"disabled:border-palette-gray-7",

				"disabled:cursor-not-allowed",
				"disabled:opacity-60",

				"bg-palette-gray-1",
				"hover:bg-palette-gray-3",
				"focus:bg-palette-gray-3",
				"active:bg-palette-gray-4",
				"[&[data-pressed='true']]:bg-palette-gray-4",
				"disabled:bg-palette-gray-1",

				"[&[data-state=checked]]:bg-palette-brand-3",
				"[&[data-state=checked]]:hover:bg-palette-gray-3",
				"[&[data-state=checked]]:focus:bg-palette-gray-3",
				"[&[data-state=checked]]:active:bg-palette-gray-4",
				"[&[data-state=checked]]:[&[data-pressed='true']]:bg-palette-gray-4",

				"text-palette-gray-12",
				"hover:text-palette-gray-12",
				"focus:text-palette-gray-12",
				"active:text-palette-gray-12",
				"[&[data-pressed='true']]:text-palette-gray-12",
				"[&[data-state=checked]]:text-palette-brand-12",
				"disabled:text-palette-gray-12",

				"[&_svg]:text-palette-gray-11",
				"[&_svg]:hover:text-palette-gray-12",
				"[&_svg]:focus:text-palette-gray-12",
				"[&_svg]:active:text-palette-gray-12",
				"[&_svg]:[&[data-pressed='true']]:text-palette-gray-12",
				"[&_svg]:[&[data-state=checked]]:text-palette-brand-9",

				"border-palette-brand-8",
				"hover:border-palette-brand-8",
				"focus:border-palette-brand-9",
				"active:border-palette-brand-9",
				"[&[data-pressed='true']]:border-palette-brand-9",
				"disabled:border-palette-gray-8",

				"focus-within:outline-palette-brand-9",
				"focus-within:outline",
				"focus-within:outline-2",
				"focus-within:-outline-offset-2",
				"focus-within:outline-0",
				"focus-within:-outline-offset-0",

				"[&[data-state=checked]]:outline-palette-brand-9",
				"[&[data-state=checked]]:outline",
				"[&[data-state=checked]]:outline-1",
				"[&[data-state=checked]]:-outline-offset-1",

				"[&[data-state=checked]]:focus-within:outline-2",
				"[&[data-state=checked]]:focus-within:-outline-offset-2",
				className
			)}
			{...props}
		/>
	);
});
RadioButtonGroupItem.displayName = "RadioButtonGroupItem";

export { RadioButtonGroup, RadioButtonGroupItem };
